const DEFAULT_PAUSE_MS = 10000;
const SCROLL_DURATION_MS = 2000;
const BOTTOM_THRESHOLD = 5;

function easeInOutQuad(t) {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

export default {
  data() {
    return {
      parcoursList: [],
      currentParcoursIndex: 0,
      scrollActive: false,
      scrollLoading: false,
      _pauseTimer: null,
      _scrollRaf: null,
    };
  },

  computed: {
    parcoursIndicator() {
      if (this.parcoursList.length === 0) {
        return "";
      }
      return `${this.currentParcoursIndex + 1} / ${this.parcoursList.length}`;
    },
    hasParcours() {
      return this.parcoursList.length > 0;
    },
  },

  methods: {
    buildParcoursList() {
      const id = this.$store.state.event.id;
      const courses = this.$store.state.event.data.list_courses || [];
      this.parcoursList = courses.map((p) => ({ id, p }));
    },

    initParcoursIndex() {
      const startP = this.$route.query.p;
      const currentCourse = this.$store.state.event.selection.course;

      if (startP) {
        const idx = this.parcoursList.findIndex((q) => q.p === startP);
        if (idx >= 0) {
          this.currentParcoursIndex = idx;
          return;
        }
      }

      if (currentCourse) {
        const idx = this.parcoursList.findIndex((q) => q.p === currentCourse);
        if (idx >= 0) {
          this.currentParcoursIndex = idx;
        }
      }
    },

    getHeaderHeight() {
      const header = this.$refs.ecranHeader;
      if (!header) {
        return 0;
      }
      const el = header.$el || header;
      return el.offsetHeight || 0;
    },

    getScrollStep() {
      return window.innerHeight - this.getHeaderHeight() - 30;
    },

    isAtBottom() {
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      return window.scrollY >= maxScroll - BOTTOM_THRESHOLD;
    },

    getPauseMs() {
      const pauseParam = this.$route.query.pause;
      if (pauseParam === undefined || pauseParam === null || pauseParam === "") {
        return DEFAULT_PAUSE_MS;
      }
      const seconds = parseFloat(pauseParam);
      if (isNaN(seconds) || seconds <= 0) {
        return DEFAULT_PAUSE_MS;
      }
      return Math.round(seconds * 1000);
    },

    pause(ms) {
      return new Promise((resolve) => {
        this._pauseTimer = setTimeout(() => {
          this._pauseTimer = null;
          resolve();
        }, ms);
      });
    },

    smoothScrollBy(distance) {
      return new Promise((resolve) => {
        const startY = window.scrollY;
        const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
        const targetY = Math.min(startY + distance, maxScroll);
        const actualDistance = targetY - startY;

        if (actualDistance <= 0) {
          resolve();
          return;
        }

        const startTime = performance.now();
        const animate = (now) => {
          if (!this.scrollActive) {
            resolve();
            return;
          }

          const elapsed = now - startTime;
          const progress = Math.min(elapsed / SCROLL_DURATION_MS, 1);
          const eased = easeInOutQuad(progress);
          window.scrollTo(0, startY + actualDistance * eased);

          if (progress < 1) {
            this._scrollRaf = requestAnimationFrame(animate);
          } else {
            this._scrollRaf = null;
            resolve();
          }
        };

        this._scrollRaf = requestAnimationFrame(animate);
      });
    },

    async loadParcoursAtIndex(index) {
      this.scrollLoading = true;
      const query = { ...this.parcoursList[index] };
      this.currentParcoursIndex = index;
      await this.$store.dispatch("event/onChangeQueryParcours", query);
      await this.$store.dispatch("event/fetch");
      window.scrollTo(0, 0);
      await this.$nextTick();
      this.scrollLoading = false;
    },

    async nextParcours() {
      const nextIndex = (this.currentParcoursIndex + 1) % this.parcoursList.length;
      await this.loadParcoursAtIndex(nextIndex);
    },

    async runScrollLoop() {
      while (this.scrollActive) {
        if (!this.$store.getters["event/has_results"]) {
          await this.pause(this.getPauseMs());
          if (!this.scrollActive) {
            break;
          }
          await this.nextParcours();
          continue;
        }

        await this.pause(this.getPauseMs());
        if (!this.scrollActive) {
          break;
        }

        if (this.isAtBottom()) {
          await this.nextParcours();
          continue;
        }

        await this.smoothScrollBy(this.getScrollStep());
      }
    },

    startAutoScroll() {
      this.buildParcoursList();
      if (this.parcoursList.length === 0) {
        return;
      }
      this.initParcoursIndex();
      this.scrollActive = true;
      this.runScrollLoop();
    },

    stopAutoScroll() {
      this.scrollActive = false;
      if (this._pauseTimer) {
        clearTimeout(this._pauseTimer);
        this._pauseTimer = null;
      }
      if (this._scrollRaf) {
        cancelAnimationFrame(this._scrollRaf);
        this._scrollRaf = null;
      }
    },
  },

  beforeDestroy() {
    this.stopAutoScroll();
  },
};
